import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import {
  BellRing, CalendarPlus, CheckCircle2, Clock, Eye, FileSignature, Inbox, Loader2, Mail, MessageCircle, Plus, Search, Trash2,
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import {
  AdminPage,
  EmptyState,
  FilterChip,
  Panel,
  PersonAvatar,
  StatCard,
  StatusChip,
  Toolbar,
  ToolbarSpacer,
  adminButton,
  useAdminPageTitle,
} from '@/components/admin/ui';
import { cn } from '@/lib/utils';
import { RepeatSettingsFields } from '@/components/admin/recurring/RepeatSettingsFields';
import {
  DEFAULT_REPEAT_SETTINGS,
  repeatSettingsError,
  toCreateSeriesInput,
  type RepeatSettings,
} from '@/components/admin/recurring/repeat-settings';
import { errorText } from '@/components/admin/recurring/visit-edit';
import { useCreateSeriesFromJob } from '@/hooks/useRecurringSeries';
import { frequencyFromText } from '@/lib/recurrence';
import {
  useEnquiries,
  useUpdateEnquiryStatus,
  useDeleteEnquiry,
  useConvertEnquiry,
  useUpdateEnquiryLead,
  type Enquiry,
} from '@/hooks/useEnquiries';
import { countFollowUpsDue, isFollowUpDue, leadSourceLabel, type LeadSource } from '@/lib/follow-ups';
import { FollowUpBadge, FollowUpPicker, LeadSourceSelect } from '@/components/admin/enquiries/FollowUpControls';
import { LogEnquiryDialog } from '@/components/admin/enquiries/LogEnquiryDialog';
import { EnquiryTypeChip } from '@/components/admin/enquiries/EnquiryTypeChip';
import {
  TENANT_ENQUIRY_TYPES,
  countsAsLead,
  isNonLeadEnquiryType,
  tenantEnquiryTypeLabel,
} from '@/lib/enquiry-types';
import { useScheduleClashCheck } from '@/hooks/useScheduleClashCheck';
import { ScheduleClashDialog } from '@/components/admin/schedule/ScheduleClashDialog';

/** `?filter=follow-ups` (the bell and the morning email link here) opens the due list. */
const FOLLOW_UP_FILTER = 'follow-ups';

const STATUS_OPTIONS = ['pending', 'contacted', 'quoted', 'accepted', 'converted', 'declined'];

/** "What's it about?" filter. 'all' = no filter; 'none' = messages without a type. */
const ALL_TYPES = 'all';
const NO_TYPE = 'none';

const VIOLET = 'hsl(var(--adm-violet))';
const MINT = 'hsl(172 46% 48%)';
const SUN = 'hsl(42 95% 52%)';
const BLUE = 'hsl(214 84% 58%)';

const isBlank = (value: string | null | undefined) => !value || value.trim() === '' || value.trim() === '—';

/** "Weekly · 1 bed flat" — skips the placeholder dashes callback requests carry. */
const serviceDetail = (enquiry: Enquiry) => {
  const text = [enquiry.frequency, enquiry.premises_size].filter((v) => !isBlank(v)).join(' · ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/** Status dropdown whose trigger is the status pill itself. */
const StatusSelect = ({
  enquiry,
  disabled,
  onChange,
  className,
}: {
  enquiry: Enquiry;
  disabled: boolean;
  onChange: (value: string) => void;
  className?: string;
}) => (
  <Select value={enquiry.status} disabled={disabled} onValueChange={onChange}>
    <SelectTrigger
      aria-label={`Status for ${enquiry.client_name}`}
      className={cn(
        'h-8 w-auto gap-1 rounded-full border-0 bg-transparent px-1 shadow-none hover:bg-muted focus:ring-2 focus:ring-ring focus:ring-offset-0 [&>svg]:h-3.5 [&>svg]:w-3.5',
        className,
      )}
    >
      <div className="flex items-center">
        <StatusChip status={enquiry.status} />
      </div>
    </SelectTrigger>
    <SelectContent>
      {STATUS_OPTIONS.map((status) => (
        <SelectItem key={status} value={status} className="text-[13px]">
          <StatusChip status={status}>{status}</StatusChip>
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const DetailItem = ({ label, children, className }: { label: string; children: ReactNode; className?: string }) => (
  <div className={cn('min-w-0', className)}>
    <p className="adm-label">{label}</p>
    <p className="mt-1 break-words text-[13.5px] font-medium text-foreground">{children}</p>
  </div>
);

const EnquiriesAdmin = () => {
  useAdminPageTitle('Enquiries');
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<string | null>(() =>
    searchParams.get('filter') === FOLLOW_UP_FILTER ? FOLLOW_UP_FILTER : null,
  );
  const [logOpen, setLogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>(ALL_TYPES);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);
  const [convertDate, setConvertDate] = useState('');
  const [convertStartTime, setConvertStartTime] = useState('09:00');
  const [convertEndTime, setConvertEndTime] = useState('10:00');
  // Enquiries that asked for regular cleans convert straight into a repeat clean.
  const [convertRepeat, setConvertRepeat] = useState<RepeatSettings>(DEFAULT_REPEAT_SETTINGS);
  const { toast } = useToast();
  const { orgId, canWrite } = useAdminOrg();
  const { assertCanWrite } = useReadOnlyGuard();

  const { data: enquiries = [], isLoading: loading } = useEnquiries(orgId);
  const updateStatusMutation = useUpdateEnquiryStatus();
  const deleteEnquiryMutation = useDeleteEnquiry();
  const convertEnquiryMutation = useConvertEnquiry();
  const createSeriesMutation = useCreateSeriesFromJob();
  const updateLeadMutation = useUpdateEnquiryLead();
  // Converting books a job, so the same opening-hours prompt as the schedule applies.
  const { confirmSchedule, clashPrompt } = useScheduleClashCheck(orgId);

  // Follow-up date / lead source edits. The open dialog keeps a local copy so
  // the picker responds straight away.
  const updateLead = async (enquiry: Enquiry, patch: { followUpOn?: string | null; leadSource?: LeadSource }) => {
    if (!assertCanWrite() || !orgId) return;
    const before = selectedEnquiry;
    if (selectedEnquiry?.id === enquiry.id) {
      setSelectedEnquiry({
        ...selectedEnquiry,
        ...(patch.followUpOn !== undefined ? { follow_up_on: patch.followUpOn } : {}),
        ...(patch.leadSource !== undefined ? { lead_source: patch.leadSource } : {}),
      });
    }
    try {
      await updateLeadMutation.mutateAsync({ enquiryId: enquiry.id, orgId, source: enquiry.source, ...patch });
      if (patch.followUpOn !== undefined) {
        toast({
          title: patch.followUpOn ? 'Follow-up set' : 'Follow-up cleared',
          description: patch.followUpOn
            ? `We'll remind you on ${format(new Date(`${patch.followUpOn}T00:00:00`), 'EEEE d MMMM')}.`
            : undefined,
        });
      } else {
        toast({ title: 'Lead source updated' });
      }
    } catch (error) {
      if (before?.id === enquiry.id) setSelectedEnquiry(before);
      toast({
        title: 'Could not save that',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const updateStatus = async (enquiry: Enquiry, newStatus: string) => {
    if (!assertCanWrite()) return;
    try {
      await updateStatusMutation.mutateAsync({
        enquiryId: enquiry.id,
        orgId: orgId!,
        newStatus,
        source: enquiry.source,
      });
      toast({ title: 'Status updated' });
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!assertCanWrite()) return;
    if (!selectedEnquiry || !orgId) return;

    try {
      await deleteEnquiryMutation.mutateAsync({
        enquiryId: selectedEnquiry.id,
        orgId,
        source: selectedEnquiry.source,
      });
      toast({ title: 'Enquiry deleted' });
      setDeleteDialogOpen(false);
      setSelectedEnquiry(null);
    } catch (error) {
      console.error('Error deleting enquiry:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete enquiry',
        variant: 'destructive',
      });
    }
  };

  const handleConvertToBooking = async () => {
    if (!assertCanWrite()) return;
    if (!selectedEnquiry || !convertDate || !convertStartTime || !convertEndTime || !orgId) {
      toast({
        title: 'Choose a date and time',
        description: 'A booking needs a scheduled date, start time and end time.',
        variant: 'destructive',
      });
      return;
    }

    const repeatProblem = repeatSettingsError(convertRepeat, convertDate);
    if (repeatProblem) {
      toast({ title: 'Check the repeat settings', description: repeatProblem, variant: 'destructive' });
      return;
    }

    const okToBook = await confirmSchedule(
      { date: convertDate, start: convertStartTime, end: convertEndTime, memberIds: [] },
      { repeating: convertRepeat.frequency !== 'none' },
    );
    if (!okToBook) return;

    try {
      const jobId = await convertEnquiryMutation.mutateAsync({
        enquiryId: selectedEnquiry.id,
        orgId,
        scheduledDate: convertDate,
        startTime: convertStartTime,
        endTime: convertEndTime,
      });
      const seriesInput = jobId ? toCreateSeriesInput(jobId, convertRepeat) : null;
      if (seriesInput) {
        try {
          await createSeriesMutation.mutateAsync(seriesInput);
          toast({ title: 'Enquiry converted to a repeat clean', description: 'The first visit and the next 12 weeks are on the calendar.' });
        } catch (seriesError) {
          toast({
            title: 'Booked, but not repeating yet',
            description: `${errorText(seriesError)} Open the job in Jobs & Calendar to set it to repeat.`,
            variant: 'destructive',
          });
        }
      } else {
        toast({ title: 'Enquiry converted to booking' });
      }
      setViewDialogOpen(false);
      setSelectedEnquiry(null);
    } catch (error) {
      toast({
        title: 'Could not convert enquiry',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const saving = deleteEnquiryMutation.isPending || convertEnquiryMutation.isPending || createSeriesMutation.isPending;

  // Client-side summaries/filters over the already-loaded list (no extra queries).
  const counts = useMemo(() => {
    const byStatus: Record<string, number> = {};
    for (const e of enquiries) byStatus[e.status] = (byStatus[e.status] ?? 0) + 1;
    return byStatus;
  }, [enquiries]);

  const daily = useMemo(() => {
    const today = new Date();
    return Array.from({ length: 14 }, (_, i) => {
      const key = format(subDays(today, 13 - i), 'yyyy-MM-dd');
      return enquiries.filter((e) => e.created_at.slice(0, 10) === key).length;
    });
  }, [enquiries]);

  const last30 = useMemo(() => {
    const cutoff = subDays(new Date(), 30).getTime();
    return enquiries.filter((e) => new Date(e.created_at).getTime() >= cutoff).length;
  }, [enquiries]);

  const filteredEnquiries = useMemo(() => {
    const term = search.trim().toLowerCase();
    return enquiries.filter((e) => {
      if (statusFilter === FOLLOW_UP_FILTER) {
        if (!isFollowUpDue(e)) return false;
      } else if (statusFilter && e.status !== statusFilter) return false;
      if (typeFilter === NO_TYPE ? Boolean(e.enquiry_type) : typeFilter !== ALL_TYPES && e.enquiry_type !== typeFilter) {
        return false;
      }
      if (!term) return true;
      return [
        e.client_name,
        e.client_email,
        e.client_phone,
        e.service_name,
        e.address,
        tenantEnquiryTypeLabel(e.enquiry_type),
      ].some((v) => v?.toLowerCase().includes(term));
    });
  }, [enquiries, statusFilter, typeFilter, search]);

  // Types present in the list, in the form's order, for the filter.
  const typesInUse = useMemo(() => {
    const present = new Set(enquiries.map((e) => e.enquiry_type ?? NO_TYPE));
    return { types: TENANT_ENQUIRY_TYPES.filter((t) => present.has(t.value)), hasUntyped: present.has(NO_TYPE) };
  }, [enquiries]);

  const followUpsDue = useMemo(() => countFollowUpsDue(enquiries), [enquiries]);

  // Job applications and partnership approaches aren't sales leads, so they're
  // left out of the won / conversion numbers (they still need a reply).
  const leads = useMemo(() => enquiries.filter(countsAsLead), [enquiries]);
  const won = leads.filter((e) => e.status === 'accepted' || e.status === 'converted').length;
  const conversionRate = leads.length > 0 ? Math.round((won / leads.length) * 100) : 0;

  const openDetails = (enquiry: Enquiry) => {
    setSelectedEnquiry(enquiry);
    const repeat = frequencyFromText(enquiry.frequency);
    setConvertRepeat({ ...DEFAULT_REPEAT_SETTINGS, frequency: repeat ?? 'none' });
    setViewDialogOpen(true);
  };
  const openDelete = (enquiry: Enquiry) => {
    setSelectedEnquiry(enquiry);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const emptyCopy = enquiries.length === 0
    ? {
        title: 'No enquiries yet',
        description: 'No enquiries yet. They will appear here when customers request quotes.',
      }
    : statusFilter === FOLLOW_UP_FILTER
      ? { title: 'No follow-ups due', description: "You're all caught up. Leads you set a follow-up date on show here on the day." }
      : { title: 'Nothing matches', description: 'Try another status or type, or clear the search.' };

  return (
    <AdminPage>
      <div className="-mb-1 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13.5px] text-muted-foreground">Manage quote requests from customers</p>
        <Button size="sm" className={adminButton.primary} disabled={!canWrite} onClick={() => setLogOpen(true)}>
          <Plus />
          Log enquiry
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 md:gap-4 xl:grid-cols-4">
        <StatCard
          label="Needs reply"
          icon={<Clock />}
          value={counts.pending ?? 0}
          hint="pending"
          trend={isMobile ? undefined : daily}
          trendType="bar"
          color={SUN}
        />
        <StatCard
          label="In conversation"
          icon={<MessageCircle />}
          value={(counts.contacted ?? 0) + (counts.quoted ?? 0)}
          hint="contacted or quoted"
          color={BLUE}
        />
        <StatCard
          label="Won"
          icon={<CheckCircle2 />}
          value={won}
          hint={`${conversionRate}% conversion`}
          color={MINT}
        />
        <StatCard
          label="Last 30 days"
          icon={<Inbox />}
          value={last30}
          hint={`${enquiries.length} all time`}
          trend={isMobile ? undefined : daily}
          color={VIOLET}
        />
      </div>

      <Panel padded={false}>
        <div className="border-b border-border p-3 md:px-5 md:py-4">
          <Toolbar>
            <div
              className="-mx-1 flex w-[calc(100%+0.5rem)] items-center gap-1.5 overflow-x-auto px-1 pb-0.5 [scrollbar-width:none] md:mx-0 md:w-auto md:flex-wrap md:overflow-visible md:p-0"
              role="group"
              aria-label="Filter by status"
            >
              <FilterChip
                active={statusFilter === null}
                count={enquiries.length}
                onClick={() => setStatusFilter(null)}
                className="shrink-0"
              >
                All
              </FilterChip>
              {(followUpsDue > 0 || statusFilter === FOLLOW_UP_FILTER) && (
                <FilterChip
                  active={statusFilter === FOLLOW_UP_FILTER}
                  count={followUpsDue}
                  onClick={() => setStatusFilter(statusFilter === FOLLOW_UP_FILTER ? null : FOLLOW_UP_FILTER)}
                  className="shrink-0"
                >
                  <BellRing className="h-3.5 w-3.5" aria-hidden="true" />
                  Follow-ups due
                </FilterChip>
              )}
              {STATUS_OPTIONS.filter((status) => (counts[status] ?? 0) > 0 || status === 'pending').map((status) => (
                <FilterChip
                  key={status}
                  active={statusFilter === status}
                  count={counts[status] ?? 0}
                  onClick={() => setStatusFilter(statusFilter === status ? null : status)}
                  className="shrink-0 capitalize"
                >
                  {status}
                </FilterChip>
              ))}
            </div>
            <ToolbarSpacer />
            {(typesInUse.types.length > 0 || typeFilter !== ALL_TYPES) && (
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger aria-label="Filter by enquiry type" className="h-9 w-full text-[13px] md:w-[200px]">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_TYPES} className="text-[13px]">All types</SelectItem>
                  {typesInUse.types.map((type) => (
                    <SelectItem key={type.value} value={type.value} className="text-[13px]">
                      {type.label}
                    </SelectItem>
                  ))}
                  {typesInUse.hasUntyped && (
                    <SelectItem value={NO_TYPE} className="text-[13px]">No type (quotes and callbacks)</SelectItem>
                  )}
                </SelectContent>
              </Select>
            )}
            <label className="adm-search w-full md:w-[260px]">
              <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="sr-only">Search enquiries</span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search name, email, service…"
              />
            </label>
          </Toolbar>
        </div>

        {/* Mobile list */}
        <div className="md:hidden">
          {filteredEnquiries.length === 0 ? (
            <EmptyState icon={<Inbox />} title={emptyCopy.title} description={emptyCopy.description} />
          ) : (
            <div className="adm-divide">
              {filteredEnquiries.map((enquiry) => (
                <div key={enquiry.id} className="space-y-3 px-4 py-3.5">
                  <div className="flex items-start gap-3">
                    <PersonAvatar name={enquiry.client_name} size={36} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="truncate text-[13.5px] font-semibold text-foreground">{enquiry.client_name}</p>
                        <p className="shrink-0 text-[11.5px] text-muted-foreground">
                          {format(new Date(enquiry.created_at), 'dd MMM')}
                        </p>
                      </div>
                      <p className="truncate text-[12.5px] text-muted-foreground">{enquiry.client_email}</p>
                      <p className="mt-1 truncate text-[12.5px] text-foreground">
                        {enquiry.service_name}
                        {serviceDetail(enquiry) && (
                          <span className="text-muted-foreground"> · {serviceDetail(enquiry)}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pl-12">
                    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                      <StatusSelect
                        enquiry={enquiry}
                        disabled={!canWrite}
                        onChange={(value) => updateStatus(enquiry, value)}
                        className="-ml-1"
                      />
                      <EnquiryTypeChip type={enquiry.enquiry_type} />
                      <FollowUpBadge lead={enquiry} />
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={adminButton.iconGhost}
                        aria-label="View details"
                        onClick={() => openDetails(enquiry)}
                      >
                        <Eye />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={cn(adminButton.iconGhost, 'hover:bg-destructive/10 hover:text-destructive')}
                        aria-label="Delete enquiry"
                        disabled={!canWrite}
                        onClick={() => openDelete(enquiry)}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block">
          <Table className="adm-table">
            <TableHeader>
              <TableRow>
                <TableHead className="pl-5">Client</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Received</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[96px] pr-5 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEnquiries.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={5}>
                    <EmptyState icon={<Inbox />} title={emptyCopy.title} description={emptyCopy.description} />
                  </TableCell>
                </TableRow>
              ) : (
                filteredEnquiries.map((enquiry) => (
                  <TableRow key={enquiry.id} className="group">
                    <TableCell className="pl-5">
                      <div className="flex min-w-0 items-center gap-3">
                        <PersonAvatar name={enquiry.client_name} size={34} />
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-foreground">{enquiry.client_name}</p>
                          <p className="truncate text-[12px] text-muted-foreground">{enquiry.client_email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <p className="font-medium text-foreground">{enquiry.service_name}</p>
                        <EnquiryTypeChip type={enquiry.enquiry_type} />
                      </div>
                      <p className="text-[12px] text-muted-foreground">{serviceDetail(enquiry) || '—'}</p>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <p className="adm-num text-foreground">{format(new Date(enquiry.created_at), 'dd MMM yyyy')}</p>
                      <p className="adm-num text-[12px] text-muted-foreground">
                        {format(new Date(enquiry.created_at), 'HH:mm')} · {leadSourceLabel(enquiry.lead_source)}
                      </p>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <StatusSelect
                          enquiry={enquiry}
                          disabled={!canWrite}
                          onChange={(value) => updateStatus(enquiry, value)}
                          className="-ml-1"
                        />
                        <FollowUpBadge lead={enquiry} />
                      </div>
                    </TableCell>
                    <TableCell className="pr-5">
                      <div className="flex items-center justify-end gap-0.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          className={adminButton.iconGhost}
                          onClick={() => openDetails(enquiry)}
                          title="View details"
                          aria-label="View details"
                        >
                          <Eye />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={cn(adminButton.iconGhost, 'hover:bg-destructive/10 hover:text-destructive')}
                          disabled={!canWrite}
                          onClick={() => openDelete(enquiry)}
                          title="Delete enquiry"
                          aria-label="Delete enquiry"
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {filteredEnquiries.length > 0 && (
          <div className="border-t border-border px-4 py-3 text-[12px] text-muted-foreground md:px-5">
            Showing {filteredEnquiries.length} of {enquiries.length}
          </div>
        )}
      </Panel>

      {/* View Enquiry Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[17px]">Enquiry Details</DialogTitle>
            <DialogDescription className="sr-only">Contact details and requested service for this enquiry.</DialogDescription>
          </DialogHeader>
          {selectedEnquiry && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/40 p-3">
                <PersonAvatar name={selectedEnquiry.client_name} size={40} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold text-foreground">{selectedEnquiry.client_name}</p>
                  <p className="truncate text-[12.5px] text-muted-foreground">
                    Submitted {format(new Date(selectedEnquiry.created_at), 'dd MMM yyyy at HH:mm')}
                  </p>
                </div>
                <StatusChip status={selectedEnquiry.status} />
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                <DetailItem label="Client Name">{selectedEnquiry.client_name}</DetailItem>
                <DetailItem label="Email">{selectedEnquiry.client_email}</DetailItem>
                <DetailItem label="Phone">{selectedEnquiry.client_phone || '—'}</DetailItem>
                <DetailItem label="Service">{selectedEnquiry.service_name}</DetailItem>
                {selectedEnquiry.enquiry_type && (
                  <div className="col-span-2 min-w-0">
                    <p className="adm-label">Enquiry about</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <EnquiryTypeChip type={selectedEnquiry.enquiry_type} />
                      <span className="text-[13.5px] font-medium text-foreground">
                        {tenantEnquiryTypeLabel(selectedEnquiry.enquiry_type)}
                      </span>
                    </div>
                    {isNonLeadEnquiryType(selectedEnquiry.enquiry_type) && (
                      <p className="mt-1.5 text-[12.5px] text-muted-foreground">
                        {selectedEnquiry.enquiry_type === 'recruitment'
                          ? "Someone asking about working for you. It's not a sales lead, so it isn't counted in your conversion numbers."
                          : "A partnership approach. It's not a sales lead, so it isn't counted in your conversion numbers."}
                      </p>
                    )}
                  </div>
                )}
                <DetailItem label="Frequency" className="capitalize">{selectedEnquiry.frequency}</DetailItem>
                <DetailItem label="Premises Size">{selectedEnquiry.premises_size}</DetailItem>
                {selectedEnquiry.address && (
                  <DetailItem label="Address" className="col-span-2">{selectedEnquiry.address}</DetailItem>
                )}
                {selectedEnquiry.additional_info && (
                  <DetailItem label="Additional Information" className="col-span-2">
                    {selectedEnquiry.additional_info}
                  </DetailItem>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 rounded-xl border border-border p-4 sm:grid-cols-2" data-testid="enquiry-follow-up">
                <FollowUpPicker
                  value={selectedEnquiry.follow_up_on}
                  disabled={!canWrite || updateLeadMutation.isPending}
                  onChange={(date) => updateLead(selectedEnquiry, { followUpOn: date })}
                />
                <LeadSourceSelect
                  value={selectedEnquiry.lead_source}
                  disabled={!canWrite || updateLeadMutation.isPending}
                  onChange={(source) => updateLead(selectedEnquiry, { leadSource: source })}
                />
              </div>

              {selectedEnquiry.source === 'enquiry' && selectedEnquiry.status !== 'converted' && (
                <div className="adm-form space-y-3 rounded-xl border border-border bg-secondary/40 p-4">
                  <div className="flex items-center gap-2 text-[13.5px] font-semibold text-foreground">
                    <CalendarPlus className="h-4 w-4 text-primary" />
                    Convert to booking
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="convert-date">Date</Label>
                      <Input
                        id="convert-date"
                        type="date"
                        disabled={!canWrite}
                        value={convertDate}
                        onChange={(event) => setConvertDate(event.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="convert-start">Start</Label>
                      <Input
                        id="convert-start"
                        type="time"
                        disabled={!canWrite}
                        value={convertStartTime}
                        onChange={(event) => setConvertStartTime(event.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="convert-end">End</Label>
                      <Input
                        id="convert-end"
                        type="time"
                        disabled={!canWrite}
                        value={convertEndTime}
                        onChange={(event) => setConvertEndTime(event.target.value)}
                      />
                    </div>
                  </div>
                  <RepeatSettingsFields
                    value={convertRepeat}
                    onChange={setConvertRepeat}
                    firstVisit={convertDate}
                    disabled={!canWrite}
                    idPrefix="convert-repeat"
                  />
                  <Button
                    size="sm"
                    onClick={handleConvertToBooking}
                    disabled={!canWrite || saving}
                    className={cn(adminButton.primary, 'w-full')}
                  >
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    Convert enquiry
                  </Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" size="sm" className={adminButton.outline} onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
            {selectedEnquiry?.source === 'enquiry' && (
              <Button
                variant="outline"
                size="sm"
                className={adminButton.outline}
                disabled={!canWrite}
                onClick={() => navigate(`/admin/quotes/new?enquiry=${selectedEnquiry.id}`)}
              >
                <FileSignature />
                Create quote
              </Button>
            )}
            <Button
              size="sm"
              className={adminButton.primary}
              onClick={() => {
                window.location.href = `mailto:${selectedEnquiry?.client_email}`;
              }}
            >
              <Mail />
              Email Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LogEnquiryDialog open={logOpen} onOpenChange={setLogOpen} orgId={orgId} />
      <ScheduleClashDialog {...clashPrompt} />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[17px]">Delete Enquiry</DialogTitle>
          </DialogHeader>
          <p className="text-[13.5px] text-muted-foreground">
            Are you sure you want to delete this enquiry from {selectedEnquiry?.client_name}? This action cannot be undone.
          </p>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" size="sm" className={adminButton.outline} onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className={cn(adminButton.primary, 'bg-destructive text-destructive-foreground hover:bg-destructive/90')}
              onClick={handleDelete}
              disabled={!canWrite || saving}
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
};

export default EnquiriesAdmin;
